/**
 * Item Spotlight — Sidebar Logos + Hero Panel + Autoplay
 */

(function () {
  'use strict';

  class ItemSpotlight {
    constructor(element) {
      this.spotlight = element;
      this.spotlight.__itemSpotlight = this;
      this.autoplayTimer = null;
      this.autoplayDuration = (parseInt(element.dataset.autoplayDuration, 10) || 5) * 1000;

      this.moveLogosToSidebar();

      this.sidebar = this.spotlight.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.sidebar.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.spotlight.querySelectorAll('[role="tabpanel"]'));

      if (this.tabs.length === 0) return;

      this.bindEvents();
      this.setupInfiniteLoop();
      this.selectItem(this.tabs[0].dataset.itemId);
      this.startAutoplay();
    }

    moveLogosToSidebar() {
      var sidebar = this.spotlight.querySelector('.item-spotlight__sidebar');
      var sources = this.spotlight.querySelectorAll('[data-item-logo-source]');

      sources.forEach(function (source) {
        var btn = source.querySelector('.item-tab');
        if (btn && sidebar) {
          sidebar.appendChild(btn);
        }
        source.remove();
      });
    }

    /**
     * Clone tabs before and after the originals to create an infinite loop effect.
     * On desktop (vertical), this allows the first tab to appear centered
     * with clones of the last tabs above it.
     */
    setupInfiniteLoop() {
      var isVertical = window.getComputedStyle(this.sidebar).flexDirection === 'column';
      if (!isVertical || this.tabs.length <= 1) {
        this._infiniteSetup = false;
        return;
      }
      if (this._infiniteSetup) return;
      this._infiniteSetup = true;

      var self = this;

      // Clone all tabs and append before + after originals
      this.tabs.forEach(function (tab) {
        var cloneBefore = tab.cloneNode(true);
        var cloneAfter = tab.cloneNode(true);
        cloneBefore.classList.add('item-tab--clone');
        cloneAfter.classList.add('item-tab--clone');
        cloneBefore.setAttribute('tabindex', '-1');
        cloneAfter.setAttribute('tabindex', '-1');
        cloneBefore.setAttribute('aria-hidden', 'true');
        cloneAfter.setAttribute('aria-hidden', 'true');
        // Store reference to original item ID
        cloneBefore.dataset.cloneOf = tab.dataset.itemId;
        cloneAfter.dataset.cloneOf = tab.dataset.itemId;
        self.sidebar.insertBefore(cloneBefore, self.tabs[0]);
        self.sidebar.appendChild(cloneAfter);
      });

      // Click on clone selects the original
      this.sidebar.querySelectorAll('.item-tab--clone').forEach(function (clone) {
        clone.addEventListener('click', function () {
          self.selectItem(clone.dataset.cloneOf);
          self.restartAutoplay();
        });
      });

      // Scroll to first original tab (centered)
      this._scrollToOriginalCenter();
    }

    /** Scroll so the active original tab is centered, accounting for clones. */
    _scrollToOriginalCenter() {
      var activeTab = this.sidebar.querySelector('[role="tab"][aria-selected="true"]:not(.item-tab--clone)');
      if (!activeTab) return;

      var sidebarRect = this.sidebar.getBoundingClientRect();
      var tabRect = activeTab.getBoundingClientRect();
      var isVertical = window.getComputedStyle(this.sidebar).flexDirection === 'column';

      if (isVertical) {
        var tabCenter = tabRect.top + tabRect.height / 2;
        var sidebarCenter = sidebarRect.top + sidebarRect.height / 2;
        this.sidebar.scrollTop += (tabCenter - sidebarCenter);
      }
    }

    /** Update clone visual states to mirror the active item. */
    _updateCloneStates(itemId) {
      this.sidebar.querySelectorAll('.item-tab--clone').forEach(function (clone) {
        var isActive = clone.dataset.cloneOf === itemId;
        clone.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    updateSidebarPadding() {
      // No longer needed — infinite loop clones handle centering
    }

    bindEvents() {
      var self = this;

      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          self.selectItem(tab.dataset.itemId);
          self.restartAutoplay();
        });
      });

      this.sidebar.addEventListener('keydown', function (e) {
        var idx = self.tabs.indexOf(e.target);
        if (idx < 0) return;

        var newIdx = idx;
        var len = self.tabs.length;

        switch (e.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            newIdx = (idx + 1) % len;
            break;
          case 'ArrowUp':
          case 'ArrowLeft':
            newIdx = (idx - 1 + len) % len;
            break;
          case 'Home':
            newIdx = 0;
            break;
          case 'End':
            newIdx = len - 1;
            break;
          default:
            return;
        }

        e.preventDefault();
        self.tabs[newIdx].focus();
        self.selectItem(self.tabs[newIdx].dataset.itemId);
        self.restartAutoplay();
      });

      // Recalculate sidebar padding on resize
      var resizeTimeout;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function () {
          self.scrollTabToCenter();
        }, 150);
      });
    }

    selectItem(itemId) {
      this.tabs.forEach(function (tab) {
        var isActive = tab.dataset.itemId === itemId;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach(function (panel) {
        var isActive = panel.dataset.itemContent === itemId;
        panel.hidden = !isActive;
      });

      this._updateCloneStates(itemId);
      this.scrollTabToCenter();
    }

    /** Scroll the active original tab to the center of the sidebar. */
    scrollTabToCenter() {
      // Find the original (non-clone) active tab
      var activeTab = this.sidebar.querySelector('[role="tab"][aria-selected="true"]:not(.item-tab--clone)');
      if (!activeTab) return;

      var sidebarRect = this.sidebar.getBoundingClientRect();
      var tabRect = activeTab.getBoundingClientRect();
      var isVertical = window.getComputedStyle(this.sidebar).flexDirection === 'column';

      if (isVertical) {
        var tabCenter = tabRect.top + tabRect.height / 2;
        var sidebarCenter = sidebarRect.top + sidebarRect.height / 2;
        this.sidebar.scrollBy({ top: tabCenter - sidebarCenter, behavior: 'smooth' });
      } else {
        var tabCenterX = tabRect.left + tabRect.width / 2;
        var sidebarCenterX = sidebarRect.left + sidebarRect.width / 2;
        this.sidebar.scrollBy({ left: tabCenterX - sidebarCenterX, behavior: 'smooth' });
      }
    }

    nextItem() {
      var currentIdx = this.tabs.findIndex(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });
      var nextIdx = (currentIdx + 1) % this.tabs.length;
      this.selectItem(this.tabs[nextIdx].dataset.itemId);
    }

    startAutoplay() {
      if (this.tabs.length <= 1) return;
      this.stopAutoplay();
      var self = this;
      this.autoplayTimer = setInterval(function () {
        self.nextItem();
      }, self.autoplayDuration);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();
    }
  }

  function initAll() {
    document.querySelectorAll('[data-item-spotlight]').forEach(function (el) {
      if (!el.__itemSpotlight) {
        new ItemSpotlight(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var el = e.target.querySelector('[data-item-spotlight]');
      if (el) {
        if (el.__itemSpotlight) el.__itemSpotlight.destroy();
        el.__itemSpotlight = null;
        new ItemSpotlight(el);
      }
    });

    document.addEventListener('shopify:block:select', function (e) {
      var spotlight = e.target.closest('[data-item-spotlight]');
      if (!spotlight || !spotlight.__itemSpotlight) return;

      spotlight.__itemSpotlight.stopAutoplay();

      var panel = e.target.querySelector('[data-item-content]');
      if (panel) {
        spotlight.__itemSpotlight.selectItem(panel.dataset.itemContent);
        return;
      }

      var parentPanel = e.target.closest('[data-item-content]');
      if (parentPanel) {
        spotlight.__itemSpotlight.selectItem(parentPanel.dataset.itemContent);
      }
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      var spotlight = e.target.closest('[data-item-spotlight]');
      if (spotlight && spotlight.__itemSpotlight) {
        spotlight.__itemSpotlight.startAutoplay();
      }
    });
  }
})();
